/**
 * Landing on the panel a line was about, rather than on the top of a long page.
 *
 * A cause on the portfolio says "35 customer asks past the acknowledgement standard" and the
 * contract page has a panel for exactly that, so the link carries the panel's hash. Two things
 * then have to happen that a normal hash does for free and the router does not: the in-app
 * navigation is a `pushState`, which neither scrolls nor moves focus, and the panel does not
 * exist yet when it runs, because the page's data is still loading.
 *
 * Focus moves, not only the scroll position. A reader on a keyboard or a screen reader has to
 * arrive where the sighted reader arrives; a page that scrolls under them and leaves focus on
 * the body is a signpost for one of them only.
 */
import { useEffect } from "preact/hooks";
import type { TriageKind } from "@trellis/core";

/**
 * Which panel on a contract page each kind of cause is about. Both ends of the link read
 * this, so a renamed panel cannot leave the portfolio pointing at nothing.
 *
 * `overdue` is the one that is not its own panel: the contract page has no list of promises
 * past their date, so it lands on the open-work split, which is where that work is broken
 * down and where the route on to Structure is.
 */
export const PANEL_ANCHOR: Record<TriageKind, string> = {
  ask: "asks",
  contract: "contract-record",
  role: "roles",
  overdue: "open-work",
  cadence: "cadences",
};

/** What to call the panel in the link, so the reader knows where they are being sent. */
export const PANEL_NAME: Record<TriageKind, string> = {
  ask: "customer asks",
  contract: "contract record",
  role: "roles",
  overdue: "open work",
  cadence: "cadences",
};

/** Move the window and the focus to the element the hash names. False when there is none. */
export function focusHashTarget(hash: string): boolean {
  const id = hash.replace(/^#/, "");
  if (!id) return false;
  const el = document.getElementById(id);
  if (!el) return false;
  el.focus();
  el.scrollIntoView({ block: "start" });
  return true;
}

/**
 * Land on the hash once the page has something to land on. `ready` is the page's own view:
 * before it arrives the panel is not rendered and there is nothing to focus.
 */
export function useHashLanding(ready: boolean): void {
  const hash = typeof location === "undefined" ? "" : location.hash;
  useEffect(() => {
    if (!ready || !hash) return;
    focusHashTarget(hash);
  }, [ready, hash]);
}
